import { Fragment } from 'react';

import type * as api from '@gitbook/api-client';
import {
    Button,
    Description,
    DialogRoot,
    DialogTrigger,
    Heading,
    useDialogTriggerState,
} from '@gitbook/spine-react';

import {
    TemplateCardActions,
    TemplateCardButton,
    TemplateCardFooter,
    TemplateCardImage,
    TemplateCardPreview,
} from './TemplateCard';
import { TemplatePreviewDialog } from './TemplatePreview';

export interface SpaceTemplateCardProps {
    /**
     * The organization ID to use for the template.
     */
    organizationId: string;

    /**
     * Template to display.
     */
    template: api.SpaceTemplate;

    /**
     * Callback when the template is applied.
     */
    onApplyTemplate: (template: api.SpaceTemplate) => Promise<void>;
}

/**
 * Card to display a space template.
 */
export function SpaceTemplateCard(props: SpaceTemplateCardProps) {
    const { template, onApplyTemplate, organizationId } = props;
    const previewDialogTrigger = useDialogTriggerState();
    return (
        <Fragment key={template.id}>
            <DialogTrigger trigger={previewDialogTrigger}>
                <TemplateCardButton key={template.id}>
                    <TemplateCardPreview>
                        <TemplateCardImage
                            src={{
                                light: template.previewImage.light,
                                dark: template.previewImage.dark,
                            }}
                        />
                        <TemplateCardActions>
                            <Button
                                label="Use template"
                                onPress={() => onApplyTemplate(template)}
                            />
                            <DialogTrigger trigger={previewDialogTrigger}>
                                <Button label="Preview" kind="secondary" />
                            </DialogTrigger>
                        </TemplateCardActions>
                    </TemplateCardPreview>
                    <TemplateCardFooter>
                        <div className="flex flex-col gap-1">
                            <Heading>{template.title}</Heading>
                            <Description>{template.subtitle}</Description>
                        </div>
                    </TemplateCardFooter>
                </TemplateCardButton>
            </DialogTrigger>
            <DialogRoot {...previewDialogTrigger.rootProps}>
                <TemplatePreviewDialog
                    template={template}
                    onApplyTemplate={async () => {
                        previewDialogTrigger.close();
                        await onApplyTemplate(template);
                    }}
                    organizationId={organizationId}
                />
            </DialogRoot>
        </Fragment>
    );
}
